import type { Rng } from "../math/rng.js";
import type { MoveMods } from "../sim/movement.js";

/**
 * Per-limb health: seven independent pools plus a derived `dead` flag — where
 * you were hit and what state it left the limb in. Everything is PURE and takes
 * its randomness from an explicit `Rng`; the order and count of draws is part
 * of the contract (see `applyHit`).
 */

export type LimbId =
  | "head"
  | "thorax"
  | "stomach"
  | "armL"
  | "armR"
  | "legL"
  | "legR";

/**
 * THE wire order for limbs: `YouSnap.hp` is a `number[]` in this order, and the
 * bleeding/fracture bitmasks use bit `i` for `LIMB_IDS[i]`. Never reorder;
 * append only, behind a `PROTOCOL_VERSION` bump.
 */
export const LIMB_IDS: readonly LimbId[] = [
  "head",
  "thorax",
  "stomach",
  "armL",
  "armR",
  "legL",
  "legR",
] as const;

/**
 * Starting (and maximum) hp per limb. The head dies to one carbine round (53),
 * the thorax (85) to two — that rhythm is the feel of the gunplay.
 */
export const LIMB_MAX_HP: Record<LimbId, number> = {
  head: 35,
  thorax: 85,
  stomach: 70,
  armL: 60,
  armR: 60,
  legL: 65,
  legR: 65,
};

/** Limbs that can be fractured. Torso and head break you differently. */
const FRACTURABLE: Record<LimbId, boolean> = {
  head: false,
  thorax: false,
  stomach: false,
  armL: true,
  armR: true,
  legL: true,
  legR: true,
};

/**
 * Damage into an already-blacked limb is multiplied by this before being spread
 * over the limbs still alive.
 */
const OVERFLOW_MULT = 0.7;

/** A hit must do at least this much damage before it can open a bleed. */
const BLEED_MIN_DAMAGE = 10;
const BLEED_CHANCE = 0.25;

/** A hit must do at least this much damage before it can fracture a limb. */
const FRACTURE_MIN_DAMAGE = 25;
const FRACTURE_CHANCE = 0.2;

/** Hp per second drained by one bleeding limb. */
const BLEED_DPS = 0.8;

/**
 * A blacked bleeding limb charges its blood loss to the thorax at this fraction
 * — what makes an untreated blacked leg lethal.
 */
const BLACKED_BLEED_TRANSFER = 0.7;

export interface LimbState {
  hp: number;
  bleeding: boolean;
  fractured: boolean;
}

export interface HealthState {
  limbs: Record<LimbId, LimbState>;
  /** Derived: head or thorax at 0. Stored so callers never re-derive it. */
  dead: boolean;
}

export interface HitOutcome {
  /** The NEW state. `applyHit` never mutates its input. */
  health: HealthState;
  /**
   * Hp actually removed from the body — clamped, so overflow off the end of a
   * limb is not counted. The hitmarker number.
   */
  damageDealt: number;
  /** True only on the transition from alive to dead. */
  killed: boolean;
}

export function createFullHealth(): HealthState {
  return {
    limbs: {
      head: { hp: LIMB_MAX_HP.head, bleeding: false, fractured: false },
      thorax: { hp: LIMB_MAX_HP.thorax, bleeding: false, fractured: false },
      stomach: { hp: LIMB_MAX_HP.stomach, bleeding: false, fractured: false },
      armL: { hp: LIMB_MAX_HP.armL, bleeding: false, fractured: false },
      armR: { hp: LIMB_MAX_HP.armR, bleeding: false, fractured: false },
      legL: { hp: LIMB_MAX_HP.legL, bleeding: false, fractured: false },
      legR: { hp: LIMB_MAX_HP.legR, bleeding: false, fractured: false },
    },
    dead: false,
  };
}

/** Deep copy of the limb record, so callers can mutate the copy freely. */
function cloneLimbs(src: Record<LimbId, LimbState>): Record<LimbId, LimbState> {
  return {
    head: { hp: src.head.hp, bleeding: src.head.bleeding, fractured: src.head.fractured },
    thorax: { hp: src.thorax.hp, bleeding: src.thorax.bleeding, fractured: src.thorax.fractured },
    stomach: { hp: src.stomach.hp, bleeding: src.stomach.bleeding, fractured: src.stomach.fractured },
    armL: { hp: src.armL.hp, bleeding: src.armL.bleeding, fractured: src.armL.fractured },
    armR: { hp: src.armR.hp, bleeding: src.armR.bleeding, fractured: src.armR.fractured },
    legL: { hp: src.legL.hp, bleeding: src.legL.bleeding, fractured: src.legL.fractured },
    legR: { hp: src.legR.hp, bleeding: src.legR.bleeding, fractured: src.legR.fractured },
  };
}

/**
 * Applies one bullet's worth of damage to one limb. A limb with hp left loses
 * hp, clamped at 0; the excess is DISCARDED, which is what makes limb hp a
 * shield. A limb already at 0 overflows: `damage * 0.7` split EVENLY over every
 * limb with hp, any share beyond a limb's remainder lost (never redistributed
 * twice). Death is head-or-thorax only.
 *
 * DETERMINISM CONTRACT — the rng schedule is part of the API: (1) a bleed roll
 * only when `damage >= 10` and the limb is not bleeding; (2) a fracture roll
 * only when `damage >= 25`, the limb is an arm or leg, and it is not fractured.
 * Skipped rolls consume no draws; a dead target consumes none. Thresholds
 * compare the INCOMING damage. `bleedChanceMult` (vitality) scales the bleed
 * CHANCE only and never the schedule — the draw is still taken, so two players
 * pull the same numbers. The default 1 is the identity.
 */
export function applyHit(
  h: HealthState,
  limb: LimbId,
  damage: number,
  rng: Rng,
  bleedChanceMult = 1,
): HitOutcome {
  // The dead take no further damage — and, importantly, burn no rng draws, so
  // a corpse being shot cannot desynchronise a replay.
  if (h.dead) return { health: h, damageDealt: 0, killed: false };

  // Damage can originate from armour blunt maths and range falloff; a negative
  // or non-finite value would poison every hp pool it touches.
  const dmg = Number.isFinite(damage) && damage > 0 ? damage : 0;

  const limbs = cloneLimbs(h.limbs);
  const target = limbs[limb];
  let damageDealt = 0;

  if (target.hp > 0) {
    const next = target.hp - dmg;
    const clamped = next > 0 ? next : 0;
    damageDealt = target.hp - clamped;
    target.hp = clamped;
  } else {
    const overflow = dmg * OVERFLOW_MULT;
    let live = 0;
    for (const id of LIMB_IDS) {
      if (limbs[id].hp > 0) live++;
    }
    if (live > 0) {
      const share = overflow / live;
      // LIMB_IDS order, so the (float) accumulation order is fixed.
      for (const id of LIMB_IDS) {
        const l = limbs[id];
        if (l.hp <= 0) continue;
        const next = l.hp - share;
        const clamped = next > 0 ? next : 0;
        damageDealt += l.hp - clamped;
        l.hp = clamped;
      }
    }
  }

  // --- Roll 1: bleed. The draw is taken whatever the multiplier is. A
  // non-finite
  // multiplier is 1, a negative one is a body that cannot bleed.
  if (dmg >= BLEED_MIN_DAMAGE && !target.bleeding) {
    const mult = Number.isFinite(bleedChanceMult) ? (bleedChanceMult > 0 ? bleedChanceMult : 0) : 1;
    if (rng.next() < BLEED_CHANCE * mult) target.bleeding = true;
  }

  // --- Roll 2: fracture. Skipped when already broken, mirroring the bleed
  // rule.
  if (dmg >= FRACTURE_MIN_DAMAGE && FRACTURABLE[limb] && !target.fractured) {
    if (rng.next() < FRACTURE_CHANCE) target.fractured = true;
  }

  const dead = limbs.head.hp <= 0 || limbs.thorax.hp <= 0;
  // We returned early when `h.dead`, so `dead` here is always a fresh kill.
  return { health: { limbs, dead }, damageDealt, killed: dead };
}

/**
 * Advances bleeding by `dt` seconds. Returns the SAME reference when there is
 * nothing to do — callers use identity to know a snapshot field is dirty. Limbs
 * drain in `LIMB_IDS` order so the bleed-out tick is reproducible; a blacked
 * limb charges the THORAX, one fixed sink.
 */
export function stepHealth(h: HealthState, dt: number): HealthState {
  if (h.dead) return h;

  let anyBleed = false;
  for (const id of LIMB_IDS) {
    if (h.limbs[id].bleeding) {
      anyBleed = true;
      break;
    }
  }
  if (!anyBleed) return h;

  const limbs = cloneLimbs(h.limbs);

  for (const id of LIMB_IDS) {
    const l = limbs[id];
    if (!l.bleeding) continue;
    if (l.hp > 0) {
      const next = l.hp - BLEED_DPS * dt;
      l.hp = next > 0 ? next : 0;
    } else {
      const t = limbs.thorax;
      const next = t.hp - BLEED_DPS * BLACKED_BLEED_TRANSFER * dt;
      t.hp = next > 0 ? next : 0;
    }
  }

  return { limbs, dead: limbs.head.hp <= 0 || limbs.thorax.hp <= 0 };
}

/**
 * Movement penalty from leg damage. A leg is "bad" when blacked OR fractured —
 * the medical system tells them apart; running speed does not.
 */
export function moveModsFromHealth(h: HealthState): MoveMods {
  return moveModsFromLegs(
    h.limbs.legL.hp <= 0 || h.limbs.legL.fractured,
    h.limbs.legR.hp <= 0 || h.limbs.legR.fractured,
  );
}

/**
 * The injury curve, by the two booleans that decide it. Split out for the
 * CLIENT, which has `hp: number[]` and a fracture mask off the wire rather than
 * a `HealthState` — one curve, so prediction and authority agree. `accelMult`
 * stays 1: a wounded leg lowers speed but does not give you mass (that is
 * `moveModsFromWeight`, composed by `combineMoveMods`).
 */
export function moveModsFromLegs(legLBad: boolean, legRBad: boolean): MoveMods {
  const bad = (legLBad ? 1 : 0) + (legRBad ? 1 : 0);
  return { speedMult: bad === 0 ? 1 : bad === 1 ? 0.6 : 0.4, accelMult: 1 };
}

// ---------------------------------------------------------------------------
// Wire helpers: bleeding/fractured ride as two small integers, bit `i` meaning
// `LIMB_IDS[i]`. Here rather than `protocol/` because the bit assignment is
// health's.
// ---------------------------------------------------------------------------

export function limbMaskHas(mask: number, limb: LimbId): boolean {
  const i = LIMB_IDS.indexOf(limb);
  return i >= 0 && (mask & (1 << i)) !== 0;
}

export function limbMaskFrom(
  limbs: Record<LimbId, LimbState>,
  key: "bleeding" | "fractured",
): number {
  let mask = 0;
  for (let i = 0; i < LIMB_IDS.length; i++) {
    if (limbs[LIMB_IDS[i] as LimbId][key]) mask |= 1 << i;
  }
  return mask;
}
